import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import Button from "../../../../common/ekedc-components/button/Button";
import { SwitchButton } from "../../../../common/components/switch-button/SwitchButton";
import { TransferUnitPinModal } from "../components/TransferUnitPinModal";
import { TransferUnitCompleteModal } from "../components/TransferUnitCompleteModal";
import styles from "./transfer-unit-amount.module.scss";

// TODO: replace with /api/vending/transfer-units balance when the endpoint is wired.
const AVAILABLE_BALANCE = 0;

export const TransferUnitAmountPage: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const recipientMeter = (location.state as { recipientMeter?: string } | null)?.recipientMeter ?? "";

	const [units, setUnits] = useState("");
	const [authorized, setAuthorized] = useState(false);
	const [showPinModal, setShowPinModal] = useState(false);
	const [pinSubmitting, setPinSubmitting] = useState(false);
	const [completedAt, setCompletedAt] = useState<number | null>(null);

	const unitsNumber = Number.parseFloat(units || "0");
	const hasUnits = unitsNumber > 0;
	const exceedsBalance = unitsNumber > AVAILABLE_BALANCE;
	const isValid = hasUnits && !exceedsBalance && authorized && !!recipientMeter;

	const handleSubmit = () => {
		if (!isValid) return;
		setShowPinModal(true);
	};

	const handlePinSubmit = async (_pin: string) => {
		setPinSubmitting(true);
		// Placeholder: backend transfer-unit endpoint will be called here with `_pin`.
		setTimeout(() => {
			setPinSubmitting(false);
			setShowPinModal(false);
			setCompletedAt(Date.now());
		}, 400);
	};

	return (
		<CustomerPageLayout
			title="Transfer Details"
			onBack={() => navigate(-1)}
			containerClass={styles.layoutContainer}
			contentContainerClass={styles.layoutContent}
		>
			<form
				className={styles.page}
				onSubmit={e => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				{/* <header className={styles.headerRow}>
					<button type="button" className={styles.backButton} onClick={() => navigate(-1)} aria-label="Go back">
						<svg width="22" height="22" viewBox="0 0 22 22" fill="none">
							<path
								d="M14.6667 5.5L9.16667 11L14.6667 16.5"
								stroke="var(--app-primary-text)"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<h1 className={styles.title}>Transfer Details</h1>
				</header> */}

				<div className={styles.scrollContent}>
					<section className={styles.recipientCard}>
						<span className={styles.recipientLabel}>You are sending to meter</span>
						<span className={styles.recipientValue}>{recipientMeter || "—"}</span>
					</section>

					<section className={styles.unitsCard}>
						<div className={styles.unitsBody}>
							<label className={styles.unitsLabel} htmlFor="units-amount">
								Number of Units (kWh)
							</label>
							<div className={styles.unitsRow}>
								<NumericFormat
									id="units-amount"
									value={units}
									onValueChange={v => setUnits(v.value)}
									thousandSeparator=","
									decimalSeparator="."
									decimalScale={2}
									allowNegative={false}
									placeholder="0.0"
									inputMode="decimal"
									className={styles.unitsInput}
								/>
								<div className={styles.unitsSuffix}>kWh</div>
							</div>
							<p className={styles.unitsHint}>
								Your Unit Balance is:{" "}
								<span className={styles.unitsHintStrong}>{AVAILABLE_BALANCE.toLocaleString(undefined, { minimumFractionDigits: 1 })}</span>
							</p>
							{exceedsBalance && (
								<p className={styles.unitsError}>You can't transfer more than your available balance.</p>
							)}
						</div>
					</section>

					<section className={styles.authCard}>
						<div className={styles.authMain}>
							<span className={styles.authTitle}>Authorization</span>
							<span className={styles.authBody}>
								I authorize the transfer of these units from my meter to the recipient, and I accept the Terms &amp;
								Conditions.
							</span>
						</div>
						<SwitchButton
							isActive={authorized}
							onChange={setAuthorized}
							size="md"
							activeColor="var(--app-secondary)"
						/>
					</section>
				</div>

				<footer className={styles.footer}>
					<Button type="submit" text="Request Transfer Units" disabled={!isValid} />
				</footer>
			</form>

			{showPinModal && (
				<TransferUnitPinModal
					onClose={() => (pinSubmitting ? undefined : setShowPinModal(false))}
					onSubmit={handlePinSubmit}
					submitting={pinSubmitting}
				/>
			)}

			{completedAt !== null && (
				<TransferUnitCompleteModal
					completedAt={completedAt}
					onBackHome={() => navigate("/dashboard")}
				/>
			)}
		</CustomerPageLayout>
	);
};

export default TransferUnitAmountPage;
